import React from "react";
import { ANIMATION_CONFIG, INTERACTION_COLORS } from "../../../config";
import { compareWorkersByName } from "../../../utils/business/worker";

/**
 * Reusable simple vertical list component that lists workers. Has an optional animation
 * for when workers are added or removed, if sent the previous list of workers.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.workers - The list of worker objects to be displayed.
 * @param {Array} props.prevWorkers - The list of previous workers to compare to.
 * @param {string} props.className - Additional class names for styling the component.
 * @example
 *
 * <WorkerList
 *   workers={[{ id: 1, first: 'John', last: 'Doe' }, { id: 2, first: 'Jane', last: 'Smith' }]}
 *   prevWorkers={[{ id: 1, first: 'John', last: 'Doe', }]}
 *   className="custom-class"
 * />
 *
 * @returns {JSX.Element} Vertical worker name list.
 */
const WorkerList = ({ workers, prevWorkers = null, className }) => {
  const combinedWorkers = prevWorkers
    ? [
        ...new Map(
          [...prevWorkers, ...workers].map((worker) => [worker.id, worker])
        ).values(),
      ]
    : workers;

  /**
   * Pulse/remove animation class when comparing to `prevWorkers`.
   *
   * @param {Object} worker Worker row.
   * @returns {string} Tailwind class string.
   */
  const getClasses = (worker) => {
    if (prevWorkers) {
      const isInPrev = prevWorkers.some(
        (prevWorker) => prevWorker.id === worker.id
      );
      const isInCurrent = workers.some(
        (currentWorker) => currentWorker.id === worker.id
      );
      if (!isInPrev && isInCurrent) {
        return "animate-pulse-bg from-transparent to-green-200 rounded-md";
      } else if (isInPrev && !isInCurrent) {
        return `${INTERACTION_COLORS.danger.bg} ${ANIMATION_CONFIG.scale} scale-0 h-0`;
      }
    }
    return "transparent";
  };

  combinedWorkers.sort(compareWorkersByName);

  if (workers.length === 0)
    return <div className={`${className} mx-2 text-gray-400`}>No workers selected</div>;

  if (combinedWorkers.length > 0)
    return (
      <div className={className}>
        {combinedWorkers.map((worker, i) => (
          <div className={getClasses(worker)} key={i}>
            <p className="px-2 my-1" style={{ lineHeight: "1" }}>
              {worker.first} {worker.last}
            </p>
          </div>
        ))}
      </div>
    );
};

export default WorkerList;
